import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/baseUi/dropdown-menu"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { BoxDashboardPaginatedBlock } from "@/modules/boxDashboard/components/boxDashboardPaginatedBlock"
import { LoadingBlock } from "@/modules/boxDashboard/components/loadingBlock"
import type { RookieMilestone } from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import {
  ROOKIE_MEMBERS_PAGE_SIZE,
  useRookieMembers,
  useTotalRookies,
} from "@/modules/boxDashboard/modules/RookieMembers/hooks/useRookieMembers"
import {
  createRookieMembersBlockModel,
  type RookieMemberModel,
} from "@/modules/boxDashboard/modules/RookieMembers/models/rookieMembersModel"
import { ProfileAvatar } from "@/modules/profile/common/components/profileAvatar"
import attendeesEmptyIllustration from "@images/empty/attendees_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { ChevronDown, CircleHelp } from "lucide-react"
import { startTransition, useState } from "react"

const MILESTONE_OPTIONS: { value: RookieMilestone; label: string }[] = [
  { value: "UNDER_15_DAYS", label: word("boxDashboard.rookies.filter.under15Days") },
  { value: "UNDER_30_DAYS", label: word("boxDashboard.rookies.filter.under30Days") },
  { value: "UNDER_45_DAYS", label: word("boxDashboard.rookies.filter.under45Days") },
  { value: "UNDER_60_DAYS", label: word("boxDashboard.rookies.filter.under60Days") },
]

export const RookieMembersBlock = () => {
  const [currentPage, setCurrentPage] = useState(0)
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedMilestone, setSelectedMilestone] = useState<RookieMilestone>("UNDER_15_DAYS")

  const { members, totalCount, isFetching } = useRookieMembers(selectedMilestone, currentPage)
  const { total: totalRookies } = useTotalRookies()
  const { navigateToMemberDetailsInfo } = useMemberSideBar()

  const model = createRookieMembersBlockModel({
    members,
    totalCount,
    currentPage,
    pageSize: ROOKIE_MEMBERS_PAGE_SIZE,
    isExpanded,
  })

  const handleMemberClick = (memberId: string) => {
    navigateToMemberDetailsInfo(memberId, {})
  }

  const selectedOption = MILESTONE_OPTIONS.find((opt) => opt.value === selectedMilestone)

  const handleMilestoneChange = (milestone: RookieMilestone) => {
    startTransition(() => {
      setSelectedMilestone(milestone)
      setCurrentPage(0)
      setIsExpanded(false)
    })
  }

  const handleShowMore = () => {
    setIsExpanded(true)
  }

  const handleShowLess = () => {
    startTransition(() => {
      setIsExpanded(false)
      setCurrentPage(0)
    })
  }

  const header = (
    <div className="flex shrink-0 flex-col gap-1 pt-6 pr-4 pb-0 pl-6">
      <div className="flex items-center gap-2">
        <Text fontStyle="HM" className="text-textDefault-heading">
          {word("boxDashboard.rookies.title")}
        </Text>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              render={<CircleHelp className="size-6 text-textOnSurface-subtle hover:text-textOnSurface-muted" />}
            />
            <TooltipContent side="bottom" align="start" className="flex-col max-w-67 rounded-xl p-0 shadow-lg">
              <div className="flex w-full items-center gap-1 border-b border-interactive-border-neutral-default p-4">
                <CircleHelp className="size-6 text-textOnSurface-subtle" />
                <Text fontStyle="LM">{word("boxDashboard.rookies.title")}</Text>
              </div>
              <div className="p-4 pt-2">
                <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
                  {word("boxDashboard.rookies.tooltip")}
                </Text>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div
          className={
            "flex items-center justify-center rounded-full bg-interactive-background-neutral-faded-highlighted px-1.5"
          }
        >
          <Text fontStyle="BXS" className={"text-textOnSurface-default"}>
            {totalRookies}
          </Text>
        </div>
      </div>
      <Text fontStyle="BM" className="text-textOnSurface-muted">
        {word("boxDashboard.rookies.subtitle")}
      </Text>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-fit cursor-pointer items-center gap-1 outline-none">
          <Text fontStyle="BM" className="text-textOnSurface-muted">
            {selectedOption ? selectedOption.label : ""}
          </Text>
          <div className="flex h-4 min-w-5 items-center justify-center rounded-full bg-surface-background-float px-1">
            <Text fontStyle="BXS" className="text-textOnSurface-muted">
              {totalCount}
            </Text>
          </div>
          <ChevronDown className="size-6 text-textOnSurface-muted" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {MILESTONE_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => handleMilestoneChange(option.value)}
              className={option.value === selectedMilestone ? "bg-surface-foreground" : ""}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )

  return (
    <BoxDashboardPaginatedBlock
      header={header}
      isEmpty={model.isEmpty}
      isLoading={isFetching}
      loadingComponent={<LoadingBlock className="mx-6 mt-6 h-90" />}
      emptyState={<EmptyState />}
      isExpanded={isExpanded}
      showShowMore={model.showShowMore}
      showShowLess={model.showShowLess}
      showPagination={model.showPagination}
      onShowMore={handleShowMore}
      onShowLess={handleShowLess}
      totalPages={model.totalPages}
      currentPage={currentPage}
      onPageChange={(page) => startTransition(() => setCurrentPage(page))}
      collapsedHeight="h-136"
      expandedHeight="h-205"
    >
      <div className="grid grid-cols-2 gap-x-1 gap-y-3 px-6">
        {model.members.map((member) => (
          <RookieCard key={member.id} member={member} onClick={() => handleMemberClick(member.memberId)} />
        ))}
      </div>
    </BoxDashboardPaginatedBlock>
  )
}

const EmptyState = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2">
      <img src={attendeesEmptyIllustration} alt="" className="h-43.75 w-74.5" />
      <Text fontStyle="LL" className="text-center">
        {word("boxDashboard.rookies.empty.title")}
      </Text>
    </div>
  )
}

const RookieCard = ({ member, onClick }: { member: RookieMemberModel; onClick: () => void }) => {
  return (
    <button
      className="flex min-w-45 cursor-pointer flex-col overflow-hidden rounded-xl border-2 border-surface-foreground bg-interactive-background-neutral-faded-highlighted text-left hover:border-interactive-background-neutral-highlighted"
      onClick={onClick}
    >
      {/* Top section: Avatar, Name, Offer, Badge */}
      <div className="flex w-full items-center justify-between bg-linear-to-r from-(--color-cloudlyPurple-900) from-0% to-(--color-cloudlyPurple-600) to-150% p-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <ProfileAvatar className="size-6 shrink-0" profile={{ avatar: member.avatar, username: member.fullName }} />
          <div className="flex min-w-0 flex-1 flex-col">
            <Text fontStyle="LM" className="truncate">
              {member.fullName}
            </Text>
            <Text fontStyle="LS" className="truncate text-textOnSurface-muted">
              {member.purchaseName ?? "\u00A0"}
            </Text>
          </div>
        </div>
        <div className="flex items-center justify-center rounded-full bg-interactive-background-neutral-faded-highlighted px-1.5">
          <Text fontStyle="BXS" className="">
            {member.daysLabel}
          </Text>
        </div>
      </div>

      {/* Bottom section: Sessions info */}
      <div className="flex w-full gap-1 bg-surface-background p-2">
        <div className="flex flex-1 flex-col">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.rookies.lastSession")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.lastSession ?? "-"}
          </Text>
        </div>
        <div className="flex flex-1 flex-col">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.rookies.nextSession")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.nextSession ?? "-"}
          </Text>
        </div>
        <div className="flex flex-1 flex-col items-end">
          <Text fontStyle="LXS" className="text-textOnSurface-muted">
            {word("boxDashboard.rookies.totalSession")}
          </Text>
          <Text fontStyle="BXS" className="text-textOnSurface-subtle">
            {member.totalSessions}
          </Text>
        </div>
      </div>
    </button>
  )
}
